import { forwardRef, useMemo } from "react";
import { Link as RouterLink, NavLink as RouterNavLink } from "react-router-dom";

import type { PathParameters } from "../../paths";
import { path, serverResponsePaths } from "../../paths";
import { useAuthentication } from "../../providers/AuthenticationProvider";

export type DirectLink = `https://${string}` | `http://${string}` | `/${string}`;
export type LinkToParam = PathParameters | Readonly<PathParameters> | DirectLink;

type LinkProps = {
  to: LinkToParam | `mailto:${string}`;
  children?: React.ReactNode;
  newTab?: boolean;
  untrusted?: boolean;
} & JSX.IntrinsicElements["a"];

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, children, newTab, untrusted, onClick, ...props }: LinkProps, ref): JSX.Element => {
    const { user } = useAuthentication();
    const needsServerResponse =
      typeof to === "string" ||
      Object.keys(serverResponsePaths).includes(to[0] as keyof typeof serverResponsePaths) ||
      (!user && to[0].endsWith(".root"));
    const destination = typeof to === "string" ? to : path(...to);
    // Use <a> for external links and links that need a server response
    const LinkElement = needsServerResponse || !destination.startsWith("/") ? "a" : RouterLink;
    const anchorProps = useMemo(() => {
      if (LinkElement !== "a") return {};
      return {
        href: destination,
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          if (
            destination.startsWith(location.origin) ||
            typeof to === "string" ||
            !destination.startsWith("http") ||
            to[0].startsWith("up.") ||
            !user
          )
            return;
          event.preventDefault();
          location.href = path("up.auth.loginRedirect", { redirectUrl: destination });
        },
      };
    }, [user, LinkElement, destination, to, onClick]);

    newTab ??= false;
    untrusted ??= false;

    return (
      <LinkElement
        {...props}
        onClick={onClick}
        ref={ref}
        {...anchorProps}
        to={destination}
        target={newTab ? "_blank" : undefined}
        rel={newTab && untrusted ? "noopener noreferrer" : undefined}
      >
        {children}
      </LinkElement>
    );
  },
);

export default Link;

type NavLinkProps = {
  to: LinkToParam;
  children?: React.ReactNode;
  newTab?: boolean;
  className?: (props: { isActive: boolean }) => string;
} & Omit<JSX.IntrinsicElements["a"], "className">;

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, children, newTab, className, ...props }: NavLinkProps, ref): JSX.Element => {
    const needsServerResponse =
      typeof to === "string" || Object.keys(serverResponsePaths).includes(to[0] as keyof typeof serverResponsePaths);
    const destination = typeof to === "string" ? to : path(...to);
    // Use <a> for external links and links that need a server response
    const LinkElement = needsServerResponse || !destination.startsWith("/") ? "a" : RouterNavLink;

    newTab ??= false;

    if (LinkElement === "a") {
      return (
        <LinkElement
          {...props}
          ref={ref}
          href={destination}
          target={newTab ? "_blank" : undefined}
          className={className?.({ isActive: false })}
        >
          {children}
        </LinkElement>
      );
    }

    return (
      <LinkElement
        {...props}
        ref={ref}
        to={destination}
        target={newTab ? "_blank" : undefined}
        className={className}
        end
      >
        {children}
      </LinkElement>
    );
  },
);
